import axios from 'axios';
import {
  StartAssessmentResponse,
  SubmitResponseRequest,
  CompleteAssessmentRequest,
  AssessmentResult
} from '../types/assessment';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export class AssessmentAPI {
  static async startAssessment(): Promise<StartAssessmentResponse> {
    try {
      const response = await api.post('/api/insights/session/start', {
        user_email: null,
        user_name: null,
        metadata: {
          started_at: new Date().toISOString(),
          user_agent: navigator.userAgent
        }
      });
      return {
        session_id: response.data.session_id,
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours from now
      };
    } catch (error) {
      console.error('Error starting assessment:', error);
      throw new Error('Failed to start assessment');
    }
  }

  static async submitResponse(request: SubmitResponseRequest): Promise<void> {
    try {
      await api.post(`/api/insights/session/${request.session_id}/response`, {
        session_id: request.session_id,
        question_id: request.question_id,
        answer: request.answer_value,
        metadata: {
          timestamp: new Date().toISOString()
        }
      });
    } catch (error) {
      console.error('Error submitting response:', error);
      // Don't throw error here - allow offline usage
    }
  }

  static async completeAssessment(request: CompleteAssessmentRequest): Promise<AssessmentResult> {
    try {
      const leadCapturedAt = new Date().toISOString();

      await api.put(`/api/insights/session/${request.session_id}`, {
        completed: true,
        progress_percentage: 100,
        user_email: request.user_info.email,
        user_name: request.user_info.name || null,
        user_phone: request.user_info.phone || null,
        gdpr_consent: request.user_info.gdpr_consent,
        marketing_consent: request.user_info.marketing_consent,
        lead_captured_at: leadCapturedAt,
        metadata: {
          completed_at: leadCapturedAt,
          user_agent: navigator.userAgent
        }
      });

      const responses = await this.getSessionResponses(request.session_id);
      
      const assessmentResponse = await api.post('/assess', {
        responses: responses,
        user_metadata: {
          session_id: request.session_id,
          submitted_at: leadCapturedAt,
          lead_capture: {
            ...request.user_info,
            captured_at: leadCapturedAt
          }
        },
        session_id: request.session_id
      });

      if (!assessmentResponse.data.success) {
        throw new Error(assessmentResponse.data.error || 'Assessment processing failed');
      }

      const result = assessmentResponse.data.result as AssessmentResult;
      if (assessmentResponse.data.result_url) {
        result.result_url = assessmentResponse.data.result_url;
      }
      if (assessmentResponse.data.result_token) {
        result.result_token = assessmentResponse.data.result_token;
      }

      return result;
    } catch (error) {
      console.error('Error completing assessment:', error);
      throw new Error('Failed to complete assessment');
    }
  }

  static async getSession(sessionId: string): Promise<any> {
    try {
      const response = await api.get(`/api/insights/session/${sessionId}`);
      return response.data;
    } catch (error) {
      console.error('Error getting session:', error);
      throw new Error('Failed to get session');
    }
  }

  static async getSessionResponses(sessionId: string): Promise<Record<string, any>> {
    try {
      const response = await api.get(`/api/insights/session/${sessionId}/responses`);
      return response.data;
    } catch (error) {
      console.error('Error getting session responses:', error);
      return {};
    }
  }

  static async getHealthCheck(): Promise<any> {
    try {
      const response = await api.get('/health');
      return response.data;
    } catch (error) {
      console.error('Error getting health check:', error);
      throw new Error('Backend is not available');
    }
  }

  static async getResultByToken(token: string): Promise<{
    result: AssessmentResult;
    result_url?: string;
  }> {
    const response = await api.get(`/api/results/${token}`);
    if (!response.data.success || !response.data.result) {
      throw new Error(response.data.error || 'Result not found');
    }
    return {
      result: response.data.result as AssessmentResult,
      result_url: response.data.result_url,
    };
  }
}
